import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import { EmailAuthProvider, getAuth, validatePassword, type PasswordValidationStatus } from 'firebase/auth'
import ReCAPTCHA from 'react-google-recaptcha'
import * as Sentry from '@sentry/browser'
import { AuthContext } from '@/context/auth-context'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { authErrorTranslate } from '@/firebase/auth/auth-error-translate'
import { authRedirectPathFrom } from '@/auth/utils/auth-redirect'
import { CreateAccountPageContent } from './CreateAccountPageContent'
import activateReferral from '@/referral/fetch/post-activate-referral'
import { retrieveReferralCode, removeReferralCode, validateReferralCodeAndUserAccount } from '@/referral/referral-utils'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import ValidationError from '@/util/errors/validation-error'
import { continueAfterEmailPasswordSignup } from '@/onboarding/post-signup-phone-routing'
import { AuthCredentialsContext } from './auth-credentials-context'

export default function CreateAccountPage() {
  const navigate = useNavigate()
  const credentialsContext = useContext(AuthCredentialsContext)
  const [searchParams] = useSearchParams()
  const { user, linkCredential, refreshUserAccountData } = useContext(AuthContext)
  const dispatch = useAppDispatch()
  const redirectPath = authRedirectPathFrom(searchParams)
  const isSignedIn = user != null && !user.isAnonymous
  const [password, setPassword] = useState(credentialsContext?.credentials.password ?? '')
  const [passwordStatus, setPasswordStatus] = useState<PasswordValidationStatus | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [signupError, setSignupError] = useState<{ message: string; passwordRelated: boolean } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const accountCreated = useRef(false)
  const [referral, setReferral] = useState<{ code: string | null; error: string | null }>({ code: null, error: null })

  useEffect(() => {
    setReferral(retrieveReferralCode())
  }, [])

  useEffect(() => {
    if (isSignedIn && !submitting && !accountCreated.current) navigate(redirectPath ?? '/dashboard', { replace: true })
  }, [isSignedIn, submitting, navigate, redirectPath])

  useEffect(() => {
    let active = true
    setPasswordStatus(null)
    if (password) {
      void validatePassword(getAuth(), password).then(
        (status) => {
          if (active) setPasswordStatus(status)
        },
        () => {
          if (active) setPasswordStatus(null)
        }
      )
    }
    return () => {
      active = false
    }
  }, [password])

  async function handleCreateAccount(email: string, submittedPassword: string) {
    setSignupError(null)
    setSubmitting(true)
    try {
      const status = await validatePassword(getAuth(), submittedPassword)
      setPasswordStatus(status)
      if (!status.isValid) {
        setSignupError({ message: 'The password does not meet all requirements.', passwordRelated: true })
        return
      }
      if (!recaptchaToken) {
        setSignupError({ message: 'Please complete the recaptcha', passwordRelated: false })
        return
      }
      await linkCredential(EmailAuthProvider.credential(email, submittedPassword))
      accountCreated.current = true
      if (referral.code) {
        try {
          const account = await refreshUserAccountData()
          validateReferralCodeAndUserAccount(referral.code, account)
          await activateReferral(referral.code)
          dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Referral activated!', durationMs: kToastDurationShortMs }))
        } catch (error) {
          if (error instanceof ValidationError) Sentry.captureException(error)
          dispatch(
            openGlobalToast({
              type: GlobalToastType.ERROR,
              message: 'Account created, but the referral could not be activated. Paxton has been notified.',
              durationMs: kToastDurationMediumMs,
            })
          )
        } finally {
          removeReferralCode()
        }
      }
      continueAfterEmailPasswordSignup(navigate, redirectPath)
    } catch (error) {
      const isPasswordError =
        error instanceof FirebaseError && ['auth/weak-password', 'auth/invalid-password', 'auth/password-does-not-meet-requirements'].includes(error.code)
      setSignupError({
        passwordRelated: isPasswordError,
        message: isPasswordError
          ? 'The provided password is not valid. Passwords should be at least 8 characters.'
          : error instanceof FirebaseError
          ? authErrorTranslate(error)
          : 'Unexpected error. Paxton has been notified.',
      })
      Sentry.captureException(error)
    } finally {
      setSubmitting(false)
    }
  }

  if (user == null || isSignedIn) return <FullPageLoader />

  return (
    <CreateAccountPageContent
      initialCredentials={credentialsContext?.credentials}
      errorMessage={signupError?.message ?? null}
      submitting={submitting}
      passwordStatus={passwordStatus}
      referralCodeActive={referral.code != null}
      referralError={referral.error}
      onPasswordChange={(password) => {
        setPassword(password)
        setSignupError((error) => (error?.passwordRelated ? null : error))
      }}
      onCreateAccount={(email, password) => void handleCreateAccount(email, password)}
      onSignIn={(credentials) => {
        credentialsContext?.setCredentials(credentials)
        navigate(redirectPath == null ? '/sign-in' : `/sign-in?redirect=${encodeURIComponent(redirectPath)}`)
      }}
      recaptcha={
        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
            onChange={setRecaptchaToken}
            onExpired={() => setRecaptchaToken(null)}
            onErrored={() => {
              setRecaptchaToken(null)
              setSignupError({ message: 'Please complete the recaptcha', passwordRelated: false })
            }}
          />
        </div>
      }
    />
  )
}
